import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { of } from 'rxjs'
import { concatMap, toArray } from 'rxjs/operators'
import { TaskResponse, UpdateTaskPayload, CreateTaskPayload, FetchTasksParams, TaskStatus } from './task.model'

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(private http: HttpClient) {}

  all(params: FetchTasksParams = {}) {
    const url = '@api/task/retrieve/'

    return this.http.post<TaskResponse[]>(url, { ...params, sort: 'created' })
  }

  allDrafts() {
    const url = '@api/task/drafts/'

    return this.http.get<TaskResponse[]>(url)
  }

  add(payload: CreateTaskPayload) {
    const url = '@api/task/?nonotify=1'
    return this.http.post<TaskResponse>(url, { ...payload, nonotify: 1 })
  }

  addTasks(payload: CreateTaskPayload[]) {
    return of(...payload).pipe(
      concatMap((payload) => this.add(payload)),
      toArray()
    )
  }

  delete(taskId: string) {
    const url = `@api/task/delete/${taskId}`

    return this.http.delete<TaskResponse>(url)
  }

  deleteMultiple(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.delete(id)),
      toArray()
    )
  }

  update(id: string, payload: UpdateTaskPayload) {
    const url = `@api/task/${id}`

    // clear old user_id
    return this.http.put<TaskResponse>(url, { ...payload, user_id: '' })
  }

  updateMultiple(ids: string[], payload: UpdateTaskPayload) {
    const normalizedPayload = payload.user_ids ? { ...payload, user_id: '' } : payload

    return of(...ids).pipe(
      concatMap((id) => this.http.put<TaskResponse>(`@api/task/${id}`, normalizedPayload)),
      toArray()
    )
  }

  archive(id: string) {
    const url = `@api/task/${id}`
    return this.http.put<TaskResponse>(url, { archived: 1 })
  }

  archiveTasks(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.archive(id)),
      toArray()
    )
  }

  unarchive(id: string) {
    const url = `@api/task/${id}`
    return this.http.put<TaskResponse>(url, { archived: 0 })
  }

  unarchiveTasks(ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.unarchive(id)),
      toArray()
    )
  }

  changeStatus(status: TaskStatus, taskId: string) {
    const url = `@api/task/${taskId}`
    return this.http.put<TaskResponse>(url, { status })
  }

  changeStatuses(status: TaskStatus, ids: string[]) {
    return of(...ids).pipe(
      concatMap((id) => this.changeStatus(status, id)),
      toArray()
    )
  }
}
